import Button from '@base/components/src/Button';
import FormTextField from '@base/components/src/FormTextField';
import { DiscountErrorType } from '@package/core/src/shoppingCart/types';
import useCouponCode from '@package/hooks/src/useCouponCode';
import { ApiError } from '@package/services/src/ApiClient';
import { ascii, compose, FormErrorCode, required } from '@package/utils/src/formValidation';
import clsx from 'clsx';
import { Field, Form, Formik } from 'formik';

import CouponCodeFormErrors from '../CouponCodeFormErrors';
import RemoveCouponButton from '../RemoveCouponButton';

import { CouponCodeFormProps } from './types';
import styles from './CouponCodeForm.module.css';

const CouponCodeForm = (props: CouponCodeFormProps) => {
  const { id, buttonLabel, couponCodeError, className, discountCouponError, removeCouponCodeError } = props;
  const { couponCode, error, applyCouponCode, deleteCouponCode } = useCouponCode();

  const couponError =
    error ||
    (couponCodeError &&
      ({
        ...couponCodeError,
        code: couponCodeError.code.toLowerCase(),
      } as ApiError));

  return (
    <Formik
      enableReinitialize
      initialValues={{ couponCode: couponCode?.couponNumber || '' }}
      onSubmit={async (values) => {
        await applyCouponCode({ couponCode: values.couponCode.replace(/\s/g, '')});
        if (removeCouponCodeError) {
          removeCouponCodeError(DiscountErrorType.CouponError);
        }
      }}
    >
      {(formikProps) => {
        const disabled =
          !!formikProps.initialValues.couponCode &&
          formikProps.values.couponCode === formikProps.initialValues.couponCode;
        return (
          <Form id={id} className={clsx(styles.couponCodeForm, className)} onSubmit={formikProps.handleSubmit}>
            <CouponCodeFormErrors error={couponError || discountCouponError} />
            <Field
              id={`${id}-input`}
              inputClassName={styles.couponCodeInput}
              inputClasses={styles.couponCodeInputContainer}
              autoComplete="off"
              fullWidth
              name="couponCode"
              label={'Coupon Code'}
              disabled={disabled}
              validate={compose(
                required,
                ascii(() => formikProps.setFieldValue('couponCode', ''), FormErrorCode.Alphabet),
              )}
              endAdornment={
                disabled && (
                  <RemoveCouponButton
                    id={id}
                    onClick={async () => {
                      await deleteCouponCode({ couponCode: couponCode?.couponNumber || '' });
                      formikProps.setFieldValue('couponCode', '', false);
                    }}
                  />
                )
              }
              component={FormTextField}
            />
            <Button
              id={`${id}-apply-btn`}
              data-testid={`${id}-apply-btn`}
              fullWidth
              type="submit"
              size={'large'}
              variant={'outlined'}
              label={buttonLabel}
              disabled={!formikProps.isValid}
              classes={styles.applyButton}
            />
            {disabled && (
              <div
                id={`${id}-success-message`}
                className={clsx(styles.successPopup, {
                  'animate-growIn': disabled,
                })}
              >
                <span className={styles.succesMessage}>
                  <span className={styles.successMessage}>Success!</span>
                  <span className={styles.successMessageDescription}>Your coupon code has been applied</span>
                </span>
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default CouponCodeForm;
